import React, { useEffect, useState } from 'react';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import DepartmentService from '../../services/departmentService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async (page = 1, overrides = {}) => {
    setLoading(true);
    try {
      const response = await DepartmentService.getDepartments({
        page,
        limit: pagination.itemsPerPage,
        search: overrides.search ?? searchTerm,
        status: overrides.status ?? statusFilter,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setDepartments(response.data || []);
      setPagination(response.pagination || pagination);
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      status: 'active'
    });
  };

  const handleCreate = async () => {
    try {
      await DepartmentService.createDepartment(formData);
      setShowCreateModal(false);
      resetForm();
      loadDepartments();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to create department');
    }
  };

  const handleEdit = async () => {
    try {
      await DepartmentService.updateDepartment(selectedDepartment.id, formData);
      setShowEditModal(false);
      setSelectedDepartment(null);
      resetForm();
      loadDepartments();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to update department');
    }
  };

  const handleDelete = async () => {
    try {
      await DepartmentService.deleteDepartment(selectedDepartment.id);
      setShowDeleteModal(false);
      setSelectedDepartment(null);
      loadDepartments();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to delete department');
    }
  };

  const openEditModal = (row) => {
    setSelectedDepartment(row);
    setFormData({
      name: row.name || '',
      code: row.code || '',
      description: row.description || '',
      status: row.status || 'active'
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (row) => {
    setSelectedDepartment(row);
    setShowDeleteModal(true);
  };

  const columns = [
    {
      key: 'name',
      label: 'Department Name',
      required: true,
      render: (_value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Building2 size={16} color="#6b7280" />
          <span style={{ fontWeight: 500, color: '#1f2937' }}>{row.name}</span>
        </div>
      )
    },
    {
      key: 'code',
      label: 'Code',
      render: (_value, row) => (
        <span style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#1d4ed8',
          backgroundColor: '#eff6ff',
          padding: '4px 8px',
          borderRadius: '6px'
        }}>
          {row.code}
        </span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (_value, row) => (
        <span style={{ fontSize: '14px', color: '#6b7280' }}>
          {row.description || '—'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value, row) => (
        <span style={{
          fontSize: '12px',
          fontWeight: 500,
          color: row.status === 'active' ? '#047857' : '#b45309',
          backgroundColor: row.status === 'active' ? '#ecfdf5' : '#fffbeb',
          padding: '4px 10px',
          borderRadius: '999px'
        }}>
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => openEditModal(row)}
            style={{
              padding: '6px',
              backgroundColor: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#374151'
            }}
            title="Edit Department"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => openDeleteModal(row)}
            style={{
              padding: '6px',
              backgroundColor: '#fef2f2',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#dc2626'
            }}
            title="Delete Department"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const renderFormFields = () => (
    <>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
          Department Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g. Water Supply"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
          Department Code *
        </label>
        <input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          placeholder="e.g. WS"
          maxLength={20}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            textTransform: 'uppercase'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          placeholder="Optional description"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
          Status
        </label>
        <select
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
    </>
  );

  const renderModal = (title, onSave, onClose) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '520px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: '0 0 20px 0' }}>
          {title}
        </h3>
        {renderFormFields()}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
          <button onClick={onClose} className="btn-outline-cam">Cancel</button>
          <button onClick={onSave} className="btn-cam">Save</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Department Management"
        description="Manage organizational departments for your tenant"
      />
      <div className="page-container">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={20} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6b7280'
              }} />
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadDepartments(1)}
                style={{
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  width: 'min(300px, 100%)'
                }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(value);
                loadDepartments(1, { status: value });
              }}
              style={{
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => loadDepartments()} className="btn-outline-cam">
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="btn-cam"
            >
              <Plus size={16} />
              Add Department
            </button>
          </div>
        </div>

        <DataTable
          data={departments}
          columns={columns}
          title="Departments"
          loading={loading}
          pagination={true}
          pageSize={pagination.itemsPerPage || 10}
          searchable={false}
          sortable={true}
          exportable={true}
          showActions={false}
          selectable={false}
        />
      </div>

      {showCreateModal && renderModal(
        'Create Department',
        handleCreate,
        () => {
          setShowCreateModal(false);
          resetForm();
        }
      )}

      {showEditModal && renderModal(
        'Edit Department',
        handleEdit,
        () => {
          setShowEditModal(false);
          setSelectedDepartment(null);
          resetForm();
        }
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedDepartment(null);
        }}
        onConfirm={handleDelete}
        title="Delete Department"
        message={`Are you sure you want to delete "${selectedDepartment?.name}"? This cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </>
  );
};

export default DepartmentManagement;
